import { ApiError, NetworkError, type ApiErrorBody } from './errors.js';

/**
 * The HTTP client. Every request to the Orbit API goes through here.
 *
 * Three things it guarantees, so no caller has to remember them:
 *
 *   1. `credentials: 'include'` — authentication is an HttpOnly cookie the
 *      browser attaches itself. There is no token to store, and therefore no
 *      token for a cross-site script to steal.
 *   2. Every non-2xx becomes an `ApiError` with the server's stable code.
 *      Callers never inspect `response.ok`.
 *   3. Requests time out. A hung fetch with no timeout is a spinner that never
 *      stops, which is worse for the user than a clear failure.
 */

/**
 * Same-origin in development (Vite proxies `/api` to the API, so the
 * SameSite=Strict session cookie is not dropped as cross-site) and in any
 * deployment that puts both behind one hostname. Override for a split origin.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * An absolute URL for a path the **browser** fetches on its own — a document
 * opened in a tab, a download, a multipart upload that cannot go through
 * `api`.
 *
 * Every such URL has to carry the API's origin. A bare `/api/v1/...` resolves
 * against whatever host the page is on, which in production is the SPA: the
 * tab opens, the SPA's catch-all serves `index.html`, and the app appears
 * where a PDF should be. That failed only in production, because development
 * proxies `/api` through Vite and the two origins are one.
 *
 * Exported so there is one of these rather than a copy per feature — three
 * files had their own, and the two that hand a URL to the browser had both
 * got it wrong.
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}/api/v1${path.startsWith('/') ? path : `/${path}`}`;
}

const DEFAULT_TIMEOUT_MS = 20_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised as JSON. Omit for GET. */
  body?: unknown;
  /** Appended as a query string. `undefined` and `null` values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = apiUrl(path);
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function looksLikeApiError(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiErrorBody).error?.code === 'string'
  );
}

export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = 'GET', body, query, signal, timeoutMs = DEFAULT_TIMEOUT_MS, headers } = options;

  // Combine the caller's cancellation with our own timeout, so a component
  // unmounting and a slow server both abort the same request.
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const combined = signal
    ? AbortSignal.any([signal, timeoutController.signal])
    : timeoutController.signal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: combined,
    });
  } catch (cause) {
    // A caller-initiated abort is not an error worth reporting — the component
    // that asked for the data has already gone.
    if (signal?.aborted) throw cause;
    throw new NetworkError(cause);
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 204) return undefined as TResponse;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (looksLikeApiError(payload)) throw new ApiError(response.status, payload);

    // The server should always send the envelope. If it did not, something is
    // between us and it — a proxy error page, a gateway timeout. Synthesise an
    // envelope so callers still only ever handle one error type.
    throw new ApiError(response.status, {
      error: {
        code: response.status >= 500 ? 'INTERNAL_ERROR' : 'MALFORMED_REQUEST',
        message:
          response.status >= 500
            ? 'Something went wrong on our side. The team has been notified.'
            : 'That request could not be completed.',
        requestId: response.headers.get('x-request-id') ?? 'unknown',
      },
    });
  }

  return payload as TResponse;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  /**
   * Takes a body, which DELETE is allowed to and most clients forget.
   *
   * Needed because deleting an employee carries a typed confirmation and a
   * reason, and neither may travel in the query string: a reason is free
   * text somebody may well write something sensitive into, and query strings
   * end up in logs, proxies and browser history.
   */
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE', body }),
};
